/**
 * Normalize USDA/API "shouting" and mixed casing for consistent UI copy.
 * Product-style strings: title case with minor words lowered, segments after — · , restarted.
 */

const MINOR_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'nor', 'but', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'per', 'to', 'vs', 'vs.', 'from', 'with', 'into', 'via',
]);

const PACK_SIZE_RE =
  /(?:^|\s)(?:\d+(?:\.\d+)?|\.\d+)\s*(?:fl\s*oz|fluid\s*ounce|fluid\s*ounces|oz|ounce|ounces|z|gr|g|kg|mg|ml|cl|dl|l|lb|lbs|count|ct|pack|pk)(?:\s+(?:bag|box|bottle|can|carton|jar|pouch))?\b\.?/gi;

const GENERIC_TRAILING_DESCRIPTORS = new Set([
  'cereal', 'cereals',
  'cookie', 'cookies',
  'cracker', 'crackers',
  'chip', 'chips',
  'snack', 'snacks',
  'beverage', 'beverages',
  'drink', 'drinks',
  'food', 'foods',
  'product', 'products',
  'item', 'items',
  'formula', 'formulas',
  'flavor', 'flavors', 'flavored',
  'variety', 'varieties',
  'pastry', 'pastries',
  'bar', 'bars',
  'mix', 'mixes',
  'meal', 'meals',
]);

function uppercaseRatio(s) {
  const letters = String(s || '').replace(/[^a-zA-Z]/g, '');
  if (letters.length < 3) return 0;
  const upper = (letters.match(/[A-Z]/g) || []).length;
  return upper / letters.length;
}

function formatWordToken(word, forceCapitalize) {
  if (!word) return '';
  if (word.includes('-')) {
    return word
      .split('-')
      .map((p, j) => formatWordToken(p, forceCapitalize && j === 0))
      .join('-');
  }
  if (word.includes('_')) {
    return word
      .split('_')
      .map((p, j) => formatWordToken(p, forceCapitalize && j === 0))
      .join(' ');
  }
  const lw = word.toLowerCase();
  if (!forceCapitalize && MINOR_WORDS.has(lw)) return lw;
  return lw.charAt(0).toUpperCase() + lw.slice(1);
}

function titleCaseChunk(chunk) {
  const trimmed = chunk.trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/);
  return words.map((w, i) => formatWordToken(w, i === 0)).join(' ');
}

function stripPackSizes(s) {
  return String(s || '').replace(PACK_SIZE_RE, ' ').replace(/\s+/g, ' ').trim();
}

function collapseRepeatedPhrases(s) {
  const words = String(s || '').split(/\s+/).filter(Boolean);
  if (words.length < 2) return s;
  const out = [];
  let i = 0;
  while (i < words.length) {
    out.push(words[i]);
    i += 1;
    let collapsed = true;
    while (collapsed) {
      collapsed = false;
      const maxK = Math.min(out.length, words.length - i);
      for (let k = maxK; k >= 1; k -= 1) {
        const tail = out.slice(out.length - k);
        const next = words.slice(i, i + k);
        if (tail.every((word, idx) => word.toLowerCase() === next[idx].toLowerCase())) {
          i += k;
          collapsed = true;
          break;
        }
      }
    }
  }
  return out.join(' ');
}

function dropTrailingGenericDescriptors(s) {
  const parts = String(s || '').split(/\s*,\s*/).filter(Boolean);
  if (parts.length <= 1) return s;
  const kept = [];
  for (const part of parts) {
    const words = part.split(/\s+/).filter(Boolean);
    const lastWord = words[words.length - 1]?.toLowerCase().replace(/[^a-z]/g, '') || '';
    if (kept.length > 0 && words.length >= 3 && GENERIC_TRAILING_DESCRIPTORS.has(lastWord)) {
      continue;
    }
    kept.push(part);
  }
  return kept.join(', ');
}

/**
 * Product names, variant chips, history titles — may include " — ", " · ", or ", ".
 */
export function formatProductLabel(input) {
  if (input == null) return '';
  let s = String(input).replace(/\s+/g, ' ').trim();
  if (!s) return '';

  s = stripPackSizes(s);
  if (uppercaseRatio(s) > 0.65) s = s.toLowerCase();

  const parts = s.split(/(\s*—\s*|\s*·\s*|\s*,\s*)/);
  let out = '';
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (/^\s*—\s*$/.test(part)) {
      out += ' — ';
    } else if (/^\s*·\s*$/.test(part)) {
      out += ' · ';
    } else if (/^\s*,\s*$/.test(part)) {
      out += ', ';
    } else if (part.trim()) {
      out += titleCaseChunk(part);
    }
  }
  out = out.replace(/\s+/g, ' ').trim();
  out = collapseRepeatedPhrases(out);
  out = dropTrailingGenericDescriptors(out);
  return out;
}

/**
 * Normalize ALL CAPS product names embedded in running text (summaries,
 * recommendations).  Finds runs of 2+ consecutive fully-uppercase words
 * (each ≥ 2 letters) and title-cases them while preserving surrounding text.
 */
export function normalizeShoutingInText(text) {
  if (!text || typeof text !== 'string') return text || '';
  return text.replace(
    /\b([A-Z][A-Z\d]{1,}(?:\s+[A-Z][A-Z\d]{1,})+)\b/g,
    (match) => titleCaseChunk(match.toLowerCase()),
  );
}

/**
 * Ingredient lines: avoid splitting on commas (parenthetical lists). Word-boundary title case.
 */
export function formatIngredientDisplay(input) {
  if (input == null) return '';
  let s = String(input).replace(/\s+/g, ' ').trim();
  if (!s) return '';

  if (uppercaseRatio(s) > 0.65) s = s.toLowerCase();

  return s.replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
}
